import React from "react";
import { css } from "@emotion/css";
import {Checkbox} from "@octopusdeploy/design-system-components";
import {FormSection, required, Text} from "components/form";
import Summary from "components/form/Sections/Summary";
import ExpandableFormSection from "components/form/Sections/ExpandableFormSection";
import FormPaperLayout from "components/FormPaperLayout/FormPaperLayout";
import OptionalFormBaseComponent, {OptionalFormBaseComponentState} from "components/form/FormBaseComponent";
import repository from "client/repository";
import {InternalRedirect} from "components/Navigation/InternalRedirect";
import {reefsRouteLinks} from "areas/reefs/reefsRouteLinks";
import {CreateReefResource} from "client/resources/createReefResource";
import {ReefPurpose} from "client/resources/reefPurpose";
import {EnvironmentName} from "client/resources/environmentName";
import {DefaultInstanceTemplateName} from "shared/defaultInstanceTemplateName";
import {VirtualMachineType} from "client/resources/virtualMachineType";
import {cloneDeep} from "lodash";
import FormSectionHeading from "components/form/Sections/FormSectionHeading";
import {RouteComponentProps} from "react-router-dom";
import Select from "components/form/Select/Select";
import ExternalLink from "components/Navigation/ExternalLink";
import {Number} from "shared/Number";
import ActionButton, {ActionButtonType} from "components/Button";
import UnstructuredFormSection from "components/form/Sections/UnstructuredFormSection";
import {DynamicWorkerServiceResource} from "client/resources/dynamicWorkerServiceResource";
import Sensitive, {ObfuscatedPlaceholder} from "components/Sensitive/Sensitive";
import {createEmptySensitiveValue} from "shared/sensitiveValueHelper";
import {HostedInstanceTemplate} from "client/resources/hostedInstanceTemplate";
import _ = require("lodash");
import {ReefTemplate} from "client/resources/reefTemplate";
import {AzureRegionResource} from "client/resources/azureRegionResource";
import {HubResource} from "../../client/resources/hubResource";

const nextButtonStyle = css`
    text-align: end;
`;

type Props = RouteComponentProps;

const ReefNameMinLength = 6;
const ReefNameMaxLength = 16;

interface State extends OptionalFormBaseComponentState<CreateReefResource> {
    redirectTo?: string;
    dynamicWorkerServices?: DynamicWorkerServiceResource[];
    azureRegions?: AzureRegionResource[];
    templates: ReefTemplate[];
    templateSelected: boolean;
    instanceTemplates?: HostedInstanceTemplate[];
    sharedName?: string;
    templateName?: string;
    canDelete: boolean;
    deleteOrphanResources: boolean;
    hubs?: HubResource[];
    environment?: EnvironmentName;
}

class CreateReef extends OptionalFormBaseComponent<Props, State, CreateReefResource> {
    constructor(props: Props) {
        super(props);
        this.state = {
            templateSelected: false,
            templates: [],
            canDelete: false,
            deleteOrphanResources: false
        };
    }

    async componentDidMount() {
        const environment =  (await repository.Configuration.getAppInfo()).Environment;
        const model: CreateReefResource = {
            DisplayName: "",
            Name: "",
            AzureRegionId: "",
            ResourceGroupName: "",
            DynamicWorkerServiceId: "",
            HubId: "",
            CreateKubernetesClusterRequest: {
                ClusterVersion: "",
                NodeVmType: VirtualMachineType.Standard_B8ms,
                NodeDiskSizeInGB: 0,
                AvailabilityZones: 1,
                HostedScriptsVersion: "",
                NodePoolSnapshotResourceId: null,
                OverrideSupportedLimit: false,
            },
            CreateSqlServerRequestResource: {
                AdminPassword: createEmptySensitiveValue(),
                AdminUser: "",
                ElasticPoolSize: 0,
                ElasticPoolStorageSizeInMB: 0,
                ElasticPoolVCores: 0,
                ElasticPoolSingleDatabaseVCoreLimit: 0,
            },
            TopLevelDomain: "",
            EnableReporting: false,
            IsDedicated: false,
            Purpose: ReefPurpose.Customer,
            AvailableToCustomers: false,
            PoolSize: 1,
            PoolInstanceTemplate: DefaultInstanceTemplateName,
            OverprovisioningMemoryRequest: "",
            OverprovisioningReplicas: 10,
            AddressSpace: environment === "Development" ? "10.16.0.0/17" : ""
        };

        this.setState({
            model,
            cleanModel: cloneDeep(model),
            environment,

            dynamicWorkerServices: await repository.DynamicWorkerServices.list(),
            azureRegions: await repository.AzureRegions.list(true),
            templates: await repository.Configuration.getReefTemplates(),
            instanceTemplates: await repository.HostedInstances.getInstanceTemplates(),
            deleteOrphanResources: environment !== "Production"
        });

        await this.doBusyTask(async () => {
            this.setState(await this.loadData());
        });
    }

    async loadData() {
        const hubs= await repository.Hubs.list();
        return {hubs};
    }

    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo}/>;
        }

        return <FormPaperLayout
            title={`Create Reef`}
            breadcrumbTitle="Reefs"
            breadcrumbPath={reefsRouteLinks.root}
            busy={this.state.busy}
            errors={this.state.errors}
            model={this.state.model}
            cleanModel={this.state.cleanModel}
            saveButtonLabel={`Create Reef`}
            saveText="Reef Created"
            saveButtonBusyLabel={"Saving"}
            expandAllOnMount={true}
            onSaveClick={this.createReef}
        >
            {this.state.model && (!this.state.templateSelected ? this.getTemplate() : this.getDetails())}
        </FormPaperLayout>;
    }

    private getTemplate = () =>
        <>
            <ExpandableFormSection
                errorKey="SharedName"
                title="SharedName"
                summary={this.state.sharedName ? Summary.summary(this.state.sharedName) : Summary.placeholder("No shared name")}
                help={<span>Enter the name that will be shared by all major resources belonging to this Reef. Only alphanumeric, lowercase characters please. Minimum of {ReefNameMinLength} and a maximum of {ReefNameMaxLength} characters.</span>}>
                <Text label="SharedName"
                    value={this.state.sharedName}
                    onChange={sharedName => this.setState({sharedName})}
                    validate={reefNameValidator("Please enter the shared name to use for this Reef")}
                    error={this.getFieldError("SharedName")}
                />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="Template"
                title="Template"
                summary={this.state.templateName ? Summary.summary(this.state.templateName) : Summary.placeholder("No Template")}
                help={<span>Select the template for this Reef.</span>}>

                <Select value={this.state.templateName}
                        onChange={(templateName: string) => this.setState({templateName})}
                        items={this.state.templates.map(t => ({text: t.Name, value: t.Name}))}
                        fieldName="Template"
                        hintText="The Template that the Reef will be based on"
                />
            </ExpandableFormSection>

            {this.state.templateName && <UnstructuredFormSection>
                <pre>
                    {JSON.stringify(this.getSelectedTemplate(), null, 4)}
                </pre>
            </UnstructuredFormSection>}

            <UnstructuredFormSection>
                <div className={nextButtonStyle}>
                    <ActionButton type={ActionButtonType.Primary} disabled={!this.state.sharedName || !this.state.templateName} label="Next" onClick={this.applyTemplate}/>
                </div>
            </UnstructuredFormSection>
        </>;

    private getDetails = () => {
        const model = this.state.model;

        return <>
            <FormSectionHeading title="General"/>
            <ExpandableFormSection
                errorKey="Name"
                title="Name"
                summary={model.Name ? Summary.summary(model.Name) : Summary.placeholder("No name")}
                help={<span>Enter the name for this Reef.</span>}>
                <Text label="Name"
                    value={model.Name}
                    onChange={Name => this.setModelState({Name})}
                    validate={reefNameValidator("Please enter the name to use for this Reef")}
                    error={this.getFieldError("Name")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="DisplayName"
                title="Display Name"
                summary={model.DisplayName ? Summary.summary(model.DisplayName) : Summary.placeholder("No display name")}
                help={<span>Enter the display name for this Reef.</span>}>
                <Text label="Display Name"
                    value={model.DisplayName}
                    onChange={DisplayName => this.setModelState({DisplayName})}
                    validate={required("Please enter the display name to use for this Reef")}
                    error={this.getFieldError("DisplayName")}
                    autoFocus={true}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="Purpose"
                title="Purpose"
                summary={Summary.summary(model.Purpose)}
                help={<span>The type of Instances this Reef hosts.</span>}>
                <Select value={model.Purpose}
                        onChange={(value: string) => this.setModelState({Purpose: value as ReefPurpose})}
                        items={this.getAllowedPurposes().map(p => ({text: p, value: p}))}
                        fieldName="Purpose"
                        hintText="The type of Instances this Reef hosts"
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="IsDedicated"
                title="Is Dedicated"
                summary={Summary.summary(model.IsDedicated ? "Enabled" : "Disabled")}
                help={<span>Makes this Reef dedicated to a single customer.</span>}>
                <Checkbox label="Is Dedicated"
                          value={model.IsDedicated}
                          onChange={IsDedicated => this.setModelState({IsDedicated})}
                          error={this.getFieldError("IsDedicated")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="AvailableToCustomers"
                title="Available To Customers"
                summary={Summary.summary(model.AvailableToCustomers ? "Enabled" : "Disabled")}
                help={<span>Makes this Reef available/unavailable to customers.</span>}>
                <Checkbox label="Available To Customers"
                        value={model.AvailableToCustomers}
                        onChange={AvailableToCustomers => this.setModelState({AvailableToCustomers})}
                        error={this.getFieldError("AvailableToCustomers")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="EnableReporting"
                title="Enable Instance Usage Reporting"
                summary={Summary.summary(model.EnableReporting ? "Enabled" : "Disabled")}
                help={<span>Enable or disable usage reporting of Instances created in this Reef. Disable for any testing.</span>}>
                <Checkbox label="Enable Reporting"
                        value={model.EnableReporting}
                        onChange={EnableReporting => this.setModelState({EnableReporting})}
                        error={this.getFieldError("EnableReporting")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="TopLevelDomain"
                title="Top Level Domain"
                summary={model.TopLevelDomain ? Summary.summary(model.TopLevelDomain) : Summary.placeholder("testoctopus.app")}
                help={<span>Enter the top level domain for this Reef.</span>}>
                <Text label="TopLevelDomain"
                    value={model.TopLevelDomain}
                    onChange={TopLevelDomain => this.setModelState({TopLevelDomain})}
                    validate={required("Please enter the top level domain  use for this Reef")}
                    error={this.getFieldError("TopLevelDomain")}
                />
            </ExpandableFormSection>
            <FormSectionHeading title="Azure - General"/>
            <ExpandableFormSection
                errorKey="Azure Region"
                title="AzureRegion"
                summary={model.AzureRegionId ? Summary.summary(model.AzureRegionId) : Summary.placeholder("No Azure Region")}
                help={<span>Enter the Azure Region for this Reef.</span>}>

                <Select value={this.state.model.AzureRegionId}
                        onChange={(AzureRegionId: string) => this.setModelState({AzureRegionId})}
                        items={this.state.azureRegions.map(s => ({text: s.Region, value: s.Id}))}
                        fieldName="AzureRegion"
                        hintText="The Azure Region that the Reef is to be created in"
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="ResourceGroupName"
                title="ResourceGroupName"
                summary={model.ResourceGroupName ? Summary.summary(model.ResourceGroupName) : Summary.placeholder("No ResourceGroupName")}
                help={<span>Enter the resource group name for this Reef.</span>}>
                <Text label="ResourceGroupName"
                    value={model.ResourceGroupName}
                    onChange={ResourceGroupName => this.setModelState({ResourceGroupName})}
                    validate={reefNameValidator("Please enter the resource group name to use for this Reef")}
                    error={this.getFieldError("ResourceGroupName")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="AddressSpace"
                title="VNet Address Space"
                summary={model.AddressSpace ? Summary.summary(model.AddressSpace) : Summary.placeholder("No address space specified")}
                help={<span>CIDR notation, with a /17 prefix length. For development Reefs, use the default pre-populated value. For Prod or Preprod, select an available address space from the <ExternalLink href={"https://docs.google.com/spreadsheets/d/1wIMJw0byPOZbvQAHPAQv6OE9kQASBDw-xbKznBUvugY/edit?usp=sharing"}>allocation table</ExternalLink>.</span>}>
                <Text label="Reef VNet Address Space"
                      value={model.AddressSpace}
                      onChange={AddressSpace => this.setModelState({AddressSpace})}
                      validate={required("Please enter the address space to use for this Reef")}
                      error={this.getFieldError("AddressSpace")}
                />
            </ExpandableFormSection>
            <FormSectionHeading title="Azure - Storage"/>
            <FormSection
                title="StorageAccountName"
                help={<span>Storage Account Name will be auto-generated.</span>} />
            <FormSectionHeading title="Azure - SQL"/>
            <FormSection
                title="SqlServerName"
                help={<span>SQL Server Name will be auto-generated.</span>} />
            <ExpandableFormSection
                errorKey="SqlServerAdminUser"
                title="SqlServerAdminUser"
                summary={model.CreateSqlServerRequestResource.AdminUser ? Summary.summary(model.CreateSqlServerRequestResource.AdminUser) : Summary.placeholder("No SqlServerAdminUser")}
                help={<span>Enter the SQL Server admin user for this Reef.</span>}>
                <Text label="SqlServerAdminUser"
                    value={model.CreateSqlServerRequestResource.AdminUser}
                    onChange={SqlServerAdminUser => this.setModelState({CreateSqlServerRequestResource: { ...model.CreateSqlServerRequestResource, AdminUser: SqlServerAdminUser}})}
                    validate={required("Please enter the SQL Server admin user to use for this Reef")}
                    error={this.getFieldError("SqlServerAdminUser")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="SqlServerAdminPassword"
                title="SqlServerAdminPassword"
                summary={model.CreateSqlServerRequestResource.AdminPassword && model.CreateSqlServerRequestResource.AdminPassword.HasValue ? Summary.summary(ObfuscatedPlaceholder) : Summary.placeholder("No SqlServerAdminPassword")}
                help={<span>Enter the SQL Server admin password for this Reef.</span>}>
                <Sensitive label="SqlServerAdminPassword"
                    value={model.CreateSqlServerRequestResource.AdminPassword}
                    onChange={SqlServerAdminPassword => this.setModelState({CreateSqlServerRequestResource: { ...model.CreateSqlServerRequestResource, AdminPassword: SqlServerAdminPassword}})}
                    error={this.getFieldError("SqlServerAdminPassword")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="SqlServerElasticPoolVCores"
                title="SqlServerElasticPoolVCores"
                summary={model.CreateSqlServerRequestResource.ElasticPoolVCores ? Summary.summary(model.CreateSqlServerRequestResource.ElasticPoolVCores) : Summary.placeholder("No SqlServerElasticPoolVCores")}
                help={<span>Enter the SqlServerElasticPoolVCores for this Reef</span>}>
                <Number label="SqlServerElasticPoolVCores"
                        value={model.CreateSqlServerRequestResource.ElasticPoolVCores}
                        min={0}
                        onChange={SqlServerElasticPoolVCores => this.setModelState({CreateSqlServerRequestResource: { ...model.CreateSqlServerRequestResource, ElasticPoolVCores: SqlServerElasticPoolVCores}})}
                        validate={required("Please enter the SQL Server elastic pool vCores to use for this Reef")}
                        error={this.getFieldError("SqlServerElasticPoolVCores")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="SqlServerElasticPoolSingleDatabaseVCoreLimit"
                title="SqlServerElasticPoolSingleDatabaseVCoreLimit"
                summary={model.CreateSqlServerRequestResource.ElasticPoolSingleDatabaseVCoreLimit ? Summary.summary(model.CreateSqlServerRequestResource.ElasticPoolSingleDatabaseVCoreLimit) : Summary.placeholder("No SqlServerElasticPoolSingleDatabaseVCoreLimit")}
                help={<span>Enter the SqlServerElasticPoolSingleDatabaseVCoreLimit for this Reef</span>}>
                <Number label="SqlServerElasticPoolSingleDatabaseVCoreLimit"
                        value={model.CreateSqlServerRequestResource.ElasticPoolSingleDatabaseVCoreLimit}
                        min={0}
                        onChange={SqlServerElasticPoolSingleDatabaseVCoreLimit => this.setModelState({CreateSqlServerRequestResource: { ...model.CreateSqlServerRequestResource, ElasticPoolSingleDatabaseVCoreLimit: SqlServerElasticPoolSingleDatabaseVCoreLimit}})}
                        validate={required("Please enter the SQL Server elastic pool single database vCore limit to use for this Reef")}
                        error={this.getFieldError("SqlServerElasticPoolSingleDatabaseVCoreLimit")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="SqlServerElasticPoolStorageSizeInMB"
                title="SqlServerElasticPoolStorageSizeInMB"
                summary={model.CreateSqlServerRequestResource.ElasticPoolStorageSizeInMB ? Summary.summary(model.CreateSqlServerRequestResource.ElasticPoolStorageSizeInMB) : Summary.placeholder("No SqlServerElasticPoolStorageSizeInMB")}
                help={<span>Enter the SqlServerElasticPoolStorageSizeInMBfor this Reef</span>}>
                <Number label="SqlServerElasticPoolStorageSizeInMB"
                        value={model.CreateSqlServerRequestResource.ElasticPoolStorageSizeInMB}
                        min={0}
                        onChange={SqlServerElasticPoolStorageSizeInMB => this.setModelState({CreateSqlServerRequestResource: { ...model.CreateSqlServerRequestResource, ElasticPoolStorageSizeInMB: SqlServerElasticPoolStorageSizeInMB}})}
                        validate={required("Please enter the SQL Server elastic pool storage size to use for this Reef")}
                        error={this.getFieldError("SqlServerElasticPoolStorageSizeInMB")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="SqlServerElasticPoolSize"
                title="SqlServerElasticPoolSize"
                summary={model.CreateSqlServerRequestResource.ElasticPoolSize ? Summary.summary(model.CreateSqlServerRequestResource.ElasticPoolSize) : Summary.placeholder("No SqlServerElasticPoolSize")}
                help={<span>Enter the SqlServerElasticPoolSize for this Reef</span>}>
                <Number label="SqlServerElasticPoolSize"
                        value={model.CreateSqlServerRequestResource.ElasticPoolSize}
                        min={0}
                        onChange={SqlServerElasticPoolSize => this.setModelState({CreateSqlServerRequestResource: { ...model.CreateSqlServerRequestResource, ElasticPoolSize: SqlServerElasticPoolSize}})}
                        validate={required("Please enter the SQL Server elastic pool size to use for this Reef")}
                        error={this.getFieldError("SqlServerElasticPoolSize")}
                />
            </ExpandableFormSection>
            <FormSectionHeading title="Azure - Kubernetes"/>
            <FormSection
                title="KubernetesClusterName"
                help={<span>Kubernetes Cluster Name will be auto-generated.</span>} />
            <ExpandableFormSection
                errorKey="KubernetesClusterVersion"
                title="KubernetesClusterVersion"
                summary={model.CreateKubernetesClusterRequest.ClusterVersion ? Summary.summary(model.CreateKubernetesClusterRequest.ClusterVersion) : Summary.placeholder("No KubernetesClusterVersion")}
                help={<span>Enter the Kubernetes cluster version for this Reef.</span>}>
                <Text label="KubernetesClusterVersion"
                      value={model.CreateKubernetesClusterRequest.ClusterVersion}
                      onChange={KubernetesClusterVersion => this.setModelState({CreateKubernetesClusterRequest: { ...model.CreateKubernetesClusterRequest, ClusterVersion: KubernetesClusterVersion}})}
                      validate={required("Please enter the Kubernetes cluster version to use for this Reef")}
                      error={this.getFieldError("KubernetesClusterVersion")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="HostedScriptsVersion"
                title="Hosted Scripts Version"
                summary={model.CreateKubernetesClusterRequest.HostedScriptsVersion ? Summary.summary(model.CreateKubernetesClusterRequest.HostedScriptsVersion) : Summary.placeholder("No Hosted Scripts Version")}
                help={<span>Enter the Hosted Scripts version for this Kubernetes Cluster.</span>}>
                <Text label="HostedScriptsVersion"
                      value={model.CreateKubernetesClusterRequest.HostedScriptsVersion}
                      onChange={v => this.setModelState({CreateKubernetesClusterRequest: { ...model.CreateKubernetesClusterRequest, HostedScriptsVersion: v}})}
                      validate={required("Please enter the Hosted Scripts Version to use for this Reef")}
                      error={this.getFieldError("HostedScriptsVersion")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="NodePoolSnapshotResourceId"
                title="NodePool Snapshot Resource Id"
                summary={model.CreateKubernetesClusterRequest.NodePoolSnapshotResourceId ? Summary.summary(model.CreateKubernetesClusterRequest.NodePoolSnapshotResourceId) : Summary.placeholder("No NodePool Snapshot Resource Id")}
                help={<span>Enter the NodePool Snapshot Resource Id for this Kubernetes Cluster if you need to roll back to an older node image version.</span>}>
                <Text label="NodePoolSnapshotResourceId"
                      value={model.CreateKubernetesClusterRequest.NodePoolSnapshotResourceId}
                      onChange={v => this.setModelState({CreateKubernetesClusterRequest: { ...model.CreateKubernetesClusterRequest, NodePoolSnapshotResourceId: v}})}
                      error={this.getFieldError("NodePoolSnapshotResourceId")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="KubernetesNodeVmType"
                title="KubernetesNodeVmType"
                summary={model.CreateKubernetesClusterRequest.NodeVmType ? Summary.summary(model.CreateKubernetesClusterRequest.NodeVmType) : Summary.placeholder("No KubernetesNodeVmType")}
                help={<span>Enter the KubernetesNodeVmType for this Reef.</span>}>

                <Select value={_.toString(this.state.model.CreateKubernetesClusterRequest.NodeVmType)}
                        onChange={(value: string) => this.setModelState({CreateKubernetesClusterRequest: { ...model.CreateKubernetesClusterRequest, NodeVmType: value as VirtualMachineType}})}
                        items={Object.entries(VirtualMachineType).map(e => ({text: e[0], value: e[0]}))}
                        fieldName="KubernetesNodeVmType"
                        hintText="The VM type to use for K8 nodes"
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="KubernetesNodeDiskSizeInGB"
                title="KubernetesNodeDiskSizeInGB"
                summary={model.CreateKubernetesClusterRequest.NodeDiskSizeInGB ? Summary.summary(model.CreateKubernetesClusterRequest.NodeDiskSizeInGB) : Summary.placeholder("No KubernetesNodeDiskSizeInGB")}
                help={<span>Enter the <ExternalLink href="KubernetesNodeDiskSizeInGB">Azure VM size</ExternalLink> to use for the worker nodes in the Kubernetes cluster.</span>}>
                <Number label="KubernetesNodeDiskSizeInGB"
                        value={model.CreateKubernetesClusterRequest.NodeDiskSizeInGB}
                        min={0}
                        onChange={KubernetesNodeDiskSizeInGB => this.setModelState({CreateKubernetesClusterRequest: { ...model.CreateKubernetesClusterRequest, NodeDiskSizeInGB: KubernetesNodeDiskSizeInGB}})}
                        validate={required("Enter the Azure VM size to use for the Kubernetes nodes. Changing this value will require the cluster to be recreated.")}
                        error={this.getFieldError("KubernetesNodeDiskSizeInGB")}
                />
            </ExpandableFormSection>
            <ExpandableFormSection
                errorKey="AvailabilityZones"
                title="AvailabilityZones"
                summary={model.CreateKubernetesClusterRequest.AvailabilityZones ? Summary.summary(model.CreateKubernetesClusterRequest.AvailabilityZones) : Summary.placeholder("AvailabilityZones")}
                help={<span>Enter the number of availability zones to use for the nodes in the Kubernetes cluster.</span>}>
                <Number label="AvailabilityZones"
                        value={model.CreateKubernetesClusterRequest.AvailabilityZones}
                        min={1}
                        max={2}
                        onChange={KubernetesAvailabilityZones => this.setModelState({CreateKubernetesClusterRequest: { ...model.CreateKubernetesClusterRequest, AvailabilityZones: KubernetesAvailabilityZones}})}
                        validate={required("Enter the number of availability zones to use for the nodes in the Kubernetes cluster.")}
                        error={this.getFieldError("AvailabilityZones")}
                />
            </ExpandableFormSection>
            <FormSectionHeading title="Pool"/>
            {this.state.instanceTemplates && <ExpandableFormSection
                errorKey="PoolSize|PoolInstanceTemplate"
                title="Pool Configuration"
                summary={model.PoolSize ? Summary.summary(model.PoolSize) : Summary.placeholder("No Pool Size")}
                help={<span>Enter the number of hot Instances that will be on standby to assign to a new customer.</span>}>
                <Number label="Pool Size"
                    value={model.PoolSize}
                    min={0}
                    onChange={PoolSize => this.setModelState({PoolSize})}
                    validate={required("Please enter the number of Instances to maintain in the pool")}
                    error={this.getFieldError("PoolSize")}
                />
                <Select value={_.toString(this.state.model.PoolInstanceTemplate)}
                        onChange={PoolInstanceTemplate => {
                            this.setModelState({PoolInstanceTemplate});
                            this.setModelState({OverprovisioningMemoryRequest: this.state.instanceTemplates.find(t => t.Name === PoolInstanceTemplate).LegacyMemoryRequest});
                        }}
                        items={this.state.instanceTemplates.map(t => ({text: t.Name, value: t.Name}))}
                        fieldName="PoolInstanceTemplate"
                        hintText="The template used to create Instances in the pool"
                />
            </ExpandableFormSection>}

            <FormSectionHeading title="Overprovisioning"/>
            <ExpandableFormSection
                errorKey="OverprovisioningReplicas"
                title="Replicas"
                summary={model.PoolSize ? Summary.summary(model.OverprovisioningReplicas) : Summary.placeholder("No Replicas")}
                help={<span>Enter the number of replicas.</span>}>
                <Number label="Replicas"
                        value={model.OverprovisioningReplicas}
                        min={0}
                        onChange={OverprovisioningReplicas => this.setModelState({OverprovisioningReplicas})}
                        validate={required("Please enter the number of replicas")}
                        error={this.getFieldError("OverprovisioningReplicas")}
                />
            </ExpandableFormSection>

            <ExpandableFormSection
                errorKey="OverprovisioningMemoryRequest"
                title="Memory Request"
                summary={model.OverprovisioningMemoryRequest ? Summary.summary(model.OverprovisioningMemoryRequest) : Summary.placeholder("No memory request")}
                help={<span>Enter the memory request.</span>}>
                <Text label="OverprovisioningMemoryRequest"
                      value={model.OverprovisioningMemoryRequest}
                      onChange={OverprovisioningMemoryRequest => this.setModelState({OverprovisioningMemoryRequest})}
                      validate={required("Please enter the memory request for overprovisioning pods")}
                      error={this.getFieldError("OverprovisioningMemoryRequest")}
                />
            </ExpandableFormSection>


            <FormSectionHeading title="Dynamic Workers"/>
            <ExpandableFormSection
                errorKey="DynamicWorkerServiceId"
                title="Dynamic Worker Service"
                summary={model.DynamicWorkerServiceId ? Summary.summary(model.DynamicWorkerServiceId) : Summary.placeholder("No dynamic worker service selected")}
                help={<span>Enter the dynamic worker service for this Reef.</span>}>

                <Select value={this.state.model.DynamicWorkerServiceId}
                        onChange={(DynamicWorkerServiceId: string) => this.setModelState({DynamicWorkerServiceId})}
                        items={this.state.dynamicWorkerServices.map(s => ({text: s.Name, value: s.Id}))}
                        fieldName="DynamicWorkerServiceId"
                        hintText="The Azure Reef that the Reef is to be created in"
                />
            </ExpandableFormSection>
            <FormSectionHeading title="Hub"/>
            <ExpandableFormSection
                errorKey="HubId"
                title="Choose Hub"
                summary={model.HubId ? Summary.summary(this.state.hubs.find(h => h.Id == model.HubId).DisplayName) : Summary.placeholder("No Hub")}
                help={<span>Choose the Hub for this Reef.</span>}>
                <Select
                    label="Hub"
                    items={this.state.hubs.map(h => ({value: h.Id, text: h.DisplayName}))}
                    value={model.HubId}
                    onChange={HubId => this.setModelState({HubId}) } />
            </ExpandableFormSection>
        </>;
    }

    private applyTemplate = () => {
        const sharedName = this.state.sharedName;
        const template = this.getSelectedTemplate();

        const model = {...this.state.model, ...template};
        model.DisplayName = sharedName;
        model.Name = sharedName;
        model.ResourceGroupName = sharedName;

        model.CreateKubernetesClusterRequest = {
            AvailabilityZones: template.KubernetesAvailabilityZones,
            ClusterVersion: "",
            NodeDiskSizeInGB: template.KubernetesNodeDiskSizeInGB,
            NodeVmType: template.KubernetesNodeVmType,
            HostedScriptsVersion: "",
            NodePoolSnapshotResourceId: null,
            OverrideSupportedLimit: false,
        };

        model.CreateSqlServerRequestResource = {
            AdminUser: "",
            AdminPassword: createEmptySensitiveValue(),
            ElasticPoolSize: template.SqlServerElasticPoolSize,
            ElasticPoolStorageSizeInMB: template.SqlServerElasticPoolStorageSizeInMB,
            ElasticPoolVCores: template.SqlServerElasticPoolVCores,
            ElasticPoolSingleDatabaseVCoreLimit: template.SqlServerElasticPoolSingleDatabaseVCoreLimit,
        };

        this.setState({templateSelected: true, model});
    };

    private getAllowedPurposes = () => this.state.environment === EnvironmentName.Production
        ? [ReefPurpose.Customer]
        : Object.values(ReefPurpose);

    private getSelectedTemplate = () => this.state.templates.find(t => t.Name === this.state.templateName);

    private createReef = () => {
        return this.doBusyTask(async () => {
            await repository.Reefs.create(this.state.model);
            this.setState({redirectTo: reefsRouteLinks.root});
        });
    };
}

const reefNameValidator = (message: string) => {
    return (value: string) => {
        const result: number = value.length;
        if (result == 0) {
            return message;
        }
        if (result < ReefNameMinLength) {
            return `Minimum of ${ReefNameMinLength} characters!`
        }
        return result > ReefNameMaxLength ? `Maximum of ${ReefNameMaxLength} characters!` : "";
    };
};
export default CreateReef;
